import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaCookieBite } from "react-icons/fa";
import {
  CONSENT_EVENT,
  DEFAULT_CONSENT,
  getConsent,
  saveConsent,
} from "../lib/cookieConsent";
import "./CookieConsent.css";

/**
 * GDPR-style cookie notice shown on a visitor's first visit.
 * Choices are stored locally; analytics/heatmap scripts only load after
 * the visitor opts in.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);
  const bannerRef = useRef(null);
  const lastFocused = useRef(null);

  const open = useCallback((withPrefs) => {
    const existing = getConsent();
    setPrefs(existing || DEFAULT_CONSENT);
    setShowPrefs(Boolean(withPrefs));
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!getConsent()) {
      // Small delay so the banner does not compete with the hero paint.
      const t = window.setTimeout(() => open(false), 900);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const handler = () => open(true);
    window.addEventListener("zu:open-cookie-settings", handler);
    return () => window.removeEventListener("zu:open-cookie-settings", handler);
  }, [open]);

  // Focus management for keyboard and screen-reader users.
  useEffect(() => {
    if (!visible) return undefined;
    lastFocused.current = document.activeElement;
    const node = bannerRef.current;
    if (node) {
      const first = node.querySelector("button, a, input");
      if (first) first.focus();
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape" && showPrefs) {
        setShowPrefs(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (lastFocused.current && lastFocused.current.focus) {
        lastFocused.current.focus();
      }
    };
  }, [visible, showPrefs]);

  const close = () => {
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    close();
  };

  const declineAll = () => {
    saveConsent({ analytics: false, marketing: false });
    close();
  };

  const savePrefs = () => {
    saveConsent(prefs);
    close();
  };

  if (!visible) return null;

  return (
    <>
      {showPrefs && <div className="zucookie-backdrop" aria-hidden="true" />}
      <section
        ref={bannerRef}
        className="zucookie-banner"
        role="dialog"
        aria-modal={showPrefs ? "true" : "false"}
        aria-labelledby="zucookie-title"
        aria-describedby="zucookie-text"
      >
        <div className="zucookie-accent" aria-hidden="true" />

        <div className="zucookie-body">
          <div className="zucookie-copy">
            <h2 className="zucookie-title" id="zucookie-title">
              <span className="zucookie-title-icon" aria-hidden="true">
                <FaCookieBite />
              </span>
              We value your privacy
            </h2>
            <p className="zucookie-text" id="zucookie-text">
              Zetech University uses cookies to keep the site working, remember your
              preferences and understand how our pages are used. You can accept all
              cookies, decline the optional ones, or choose exactly what you are
              comfortable with. Read our{" "}
              <Link to="/privacy-policy" onClick={close}>
                Privacy Policy
              </Link>
              .
            </p>
          </div>

          <div className="zucookie-actions">
            <button
              type="button"
              className="zucookie-btn zucookie-btn-ghost"
              onClick={() => setShowPrefs((v) => !v)}
              aria-expanded={showPrefs}
              aria-controls="zucookie-prefs"
            >
              {showPrefs ? "Hide options" : "Manage preferences"}
            </button>
            <button
              type="button"
              className="zucookie-btn zucookie-btn-decline"
              onClick={declineAll}
            >
              Decline optional
            </button>
            <button
              type="button"
              className="zucookie-btn zucookie-btn-accept"
              onClick={acceptAll}
            >
              Accept all cookies
            </button>
          </div>
        </div>

        {showPrefs && (
          <div className="zucookie-prefs" id="zucookie-prefs">
            <ul className="zucookie-prefs-list">
              <li className="zucookie-pref">
                <input
                  type="checkbox"
                  id="zucookie-necessary"
                  checked
                  disabled
                  readOnly
                />
                <label htmlFor="zucookie-necessary">
                  <span className="zucookie-pref-name">
                    Strictly necessary
                    <span className="zucookie-pref-locked">Always on</span>
                  </span>
                  <span className="zucookie-pref-desc">
                    Required for security, page navigation, accessibility settings
                    and language selection. The site cannot work without them.
                  </span>
                </label>
              </li>

              <li className="zucookie-pref">
                <input
                  type="checkbox"
                  id="zucookie-analytics"
                  checked={prefs.analytics}
                  onChange={(e) =>
                    setPrefs((p) => ({ ...p, analytics: e.target.checked }))
                  }
                />
                <label htmlFor="zucookie-analytics">
                  <span className="zucookie-pref-name">Analytics &amp; performance</span>
                  <span className="zucookie-pref-desc">
                    Helps us see which programmes and pages are popular so we can
                    improve the site. Data is aggregated and anonymised.
                  </span>
                </label>
              </li>

              <li className="zucookie-pref">
                <input
                  type="checkbox"
                  id="zucookie-marketing"
                  checked={prefs.marketing}
                  onChange={(e) =>
                    setPrefs((p) => ({ ...p, marketing: e.target.checked }))
                  }
                />
                <label htmlFor="zucookie-marketing">
                  <span className="zucookie-pref-name">Marketing &amp; media</span>
                  <span className="zucookie-pref-desc">
                    Lets us show you relevant admissions campaigns and embedded
                    social or video content from our partners.
                  </span>
                </label>
              </li>
            </ul>

            <div className="zucookie-prefs-actions">
              <button
                type="button"
                className="zucookie-btn zucookie-btn-ghost"
                onClick={declineAll}
              >
                Reject all optional
              </button>
              <button
                type="button"
                className="zucookie-btn zucookie-btn-accept"
                onClick={savePrefs}
              >
                Save my choices
              </button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

export { CONSENT_EVENT };
